import { Grid, NetworkIcon, Spinner, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { Redirect } from 'react-router'
import { useHistory, useParams } from 'react-router-dom'
import styles from 'modules/Billing/Billing.module.scss'
import AdmitadWarning from 'modules/Billing/subcomponents/AdmitadWarning'
import Placeholder from 'modules/Billing/subcomponents/Placeholder'
import PaymentMethods from 'modules/Billing/subcomponents/PaymentMethods'
import { useBilling } from 'modules/Forms/Billing/useBilling'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import Billing from 'modules/Forms/Billing/hoc/Billing'
import { IS_APP_PRODUCT_GETUNIQ } from 'utils/constants/product'
import DepositCreationFeeForm
  from 'modules/Forms/Billing/DepositCreationFeeForm'
import HighRiskLabel from 'modules/Services/Network/subcomponents/HighRiskLabel'

const DepositCreationFee = () => {
  const {
    data: {
      methods: { loading, loaded, data: methods = [] } = {},
      activeMethod,
      networkAccountData: {
        status,
        loaded: accountLoaded,
        login: accountName = '',
        type = null,
      } = {},
    } = {},
    setActiveMethod,
    getPaymentMethods,
    getNetworkAccountData,
    getAccountData,
    pushMethods,
  } = useBilling('deposit_creation_fee')
  const pathGen = useGeneratePathI18n()
  const { accountId } = useParams()
  const history = useHistory()

  const { t } = useTranslation(['billing'])

  useEffect(() => {
    getNetworkAccountData({ accountId })
  }, [])

  useEffect(() => {
    if (activeMethod?.method === 'admitad') {
      getAccountData({ id: activeMethod.method })
    }
  }, [activeMethod])

  useEffect(() => {
    if (accountId && accountLoaded) {
      getPaymentMethods({ accountId })
    }
  }, [accountId, accountLoaded])

  useEffect(
    () => loaded && IS_APP_PRODUCT_GETUNIQ && pushMethods([{ method: 'admitad' }]),
    [loaded],
  )

  if (status === 404) {
    return <Redirect to={pathGen(ROUTES.SERVICES)} />
  }

  return (
    <>
      <Helmet defer={false}>
        <title>{t('head.deposit_creation_fee.title', { accountName })}</title>
      </Helmet>
      <Billing>
        {!loaded ? (
          <Placeholder />
        ) : (
          <>
            <div className={styles['billing__content-top']}>
              <Grid
                direction={'column'}
                gap={32}
                className={styles['billing__heading']}
              >
                <Grid gap={8}>
                  <Typography
                    variant={'Header-40'} noMargin component={'h1'}
                  >
                    {t('deposit_creation_fee.header')}
                  </Typography>

                  <Typography
                    variant={'Body-20'}
                    color={'Label-Secondary'}
                    noMargin
                  >
                    {t('deposit_creation_fee.description')}
                  </Typography>
                </Grid>
                <Grid
                  direction={'column'}
                  gap={8}
                >
                  <Typography
                    variant={'Body-10'}
                    color={'Primary-Secondary'}
                    noMargin
                  >
                    {t('account_login')}
                  </Typography>
                  <Grid
                    gap={8}
                    alignItems={'center'}
                    fluid={false}
                    wrap={false}
                  >
                    <NetworkIcon
                      rounded
                      name={type}
                      width={20}
                    />
                    <Typography
                      component={'div'}
                      variant={'Body-3'}
                      color={'Label-Fifth'}
                      noMargin
                    >
                      {accountName}
                    </Typography>
                    {type.match(/high-risk$/) && <HighRiskLabel size={'xs'} />}
                  </Grid>
                </Grid>
              </Grid>

              {loading && (
                <div className={styles['billing__spinner']}>
                  <Spinner color="Label-GU-Primary" />
                </div>
              )}

              {loaded && (
                <PaymentMethods
                  className={'margin-bottom-40 '}
                  handleClickSlider={setActiveMethod}
                  data={methods.map(({ method, ...rest }) => ({
                    title: t(`methods.${method}`).replace('methods.', ''),
                    method,
                    active: activeMethod?.method === method,
                    ...rest,
                  }))}
                />
              )}

              {activeMethod?.method === 'admitad' ? (
                <AdmitadWarning />
              ) : (
                <DepositCreationFeeForm
                  accountId={accountId}
                  onClose={() =>
                    history.push(
                      pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT, {
                        networkType: type,
                        accountId,
                      }),
                    )
                  }
                />
              )}
            </div>
            <div
              className={styles['billing__content-bottom']}
              dangerouslySetInnerHTML={{
                __html: t('info', {
                  link: pathGen(ROUTES.TICKET_NEW),
                }),
              }}
            />
          </>
        )}
      </Billing>
    </>
  )
}
DepositCreationFee.propTypes = {
  onClose: PropTypes.func,
}
export default DepositCreationFee
